import { Box } from '@mui/material'
import {
  TemplateOutputs,
  TemplateSummaryOutputs,
} from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'
import { useMemo } from 'react'
import { calculateGraphData } from './SequenceOfEvents.utils'
import { Graph } from 'Common/Graph/Graph'

export const colors = {
  lineColor: '#E7E7E7',
  fontColor: '#000',
  hoverBorderColor: '#000',
  hoverBackgroundColor: '#FFF',
  referenceLineColor: '#006286',
}

type SequenceOfEventsGraphProps = {
  graphData: GraphData
  capacityReferenceValue: number
  averageUtilisationPercentage: number | string | undefined
  averageUtilisationHours: number | string | undefined
}

export type GraphData = {
  graphData: {
    changeOverTime: number
    productCode: string
    productionTime: number
    inBatchChangeOverTime: number
    sequence: number
  }[]
  week: number
}[]

export const convertTemplateOutputsToGraphData = (
  templateOutputs: TemplateOutputs[]
): GraphData => {
  const groupedByWeek = templateOutputs.reduce((acc, item) => {
    if (!acc[item.weekNo]) {
      acc[item.weekNo] = []
    }

    acc[item.weekNo].push({
      changeOverTime: item.changeOverTimeInHours,
      productCode: item.skuGroupCode,
      productionTime: item.productionTimeInHrs,
      inBatchChangeOverTime: item.inBatchChangeOverTimeInHours,
      sequence: item.productionSequence,
    })

    return acc
  }, {} as Record<number, GraphData[0]['graphData']>)

  const graphData: GraphData = Object.entries(groupedByWeek).map(
    ([weekStr, unsortedData]) => ({
      week: Number(weekStr),
      graphData: unsortedData.sort((a, b) => a.sequence - b.sequence),
    })
  )

  return graphData
}

export const SequenceOfEventsGraph = ({
  graphData,
  capacityReferenceValue,
  averageUtilisationPercentage,
  averageUtilisationHours,
}: SequenceOfEventsGraphProps) => {
  const productionTimeHovertemplate = () => {
    return (
      ` %{text}<br>` +
      ` %{x}<br>` +
      ` Production time:  %{y}h<br>` +
      '<extra></extra>'
    )
  }
  const changeOverTimeHovertemplate = () => {
    return ` %{text}<br>` + ` Change over time:  %{y}h<br>` + '<extra></extra>'
  }

  const inBatchChangeOverTimeHovertemplate = () => {
    return (
      ` %{text}<br>` +
      ` in Batch Change over time:  %{y}h<br>` +
      '<extra></extra>'
    )
  }

  const data = useMemo(() => {
    const xAxisData = graphData?.map((item) => {
      return `${item.week}`
    })
    const defaultProps = {
      x: xAxisData,
      type: 'bar',
      textfont: { color: 'transparent' },
      width: 0.45,
    }

    const productionTimeProps = {
      ...defaultProps,
      name: 'Production time',
      hovertemplate: productionTimeHovertemplate(),
      showlegend: false,
      marker: {
        color: '0077b6',
      },
    }

    const changeOverTimeProps = {
      ...defaultProps,
      name: 'Changeover time',
      hovertemplate: changeOverTimeHovertemplate(),
      showlegend: false,
      marker: {
        color: 'fe7f2d',
      },
    }

    const inBatchChangeOverTimeProps = {
      ...defaultProps,
      name: 'Within Batch Changeover time',
      hovertemplate: inBatchChangeOverTimeHovertemplate(),
      showlegend: false,
      marker: {
        color: 'ffd166',
      },
    }

    const graphDataList = calculateGraphData(graphData)

    const traceList = []
    for (let i = 0; i < graphDataList.length; i++) {
      const productionTimeTrace = {
        ...productionTimeProps,
        y: graphDataList[i].productionTime,
        text: graphDataList[i].productCode,
      }

      const changeOverTimeTrace = {
        ...changeOverTimeProps,
        y: graphDataList[i].changeOverTime,
        text: graphDataList[i].changeOverText,
      }

      const inBatchChangeOverTimeTrace = {
        ...inBatchChangeOverTimeProps,
        y: graphDataList[i].inBatchChangeOverTime,
        text: graphDataList[i].changeOverText,
      }
      if (!i) {
        productionTimeTrace.showlegend = true
        changeOverTimeTrace.showlegend = true
        inBatchChangeOverTimeTrace.showlegend = true
      }
      traceList.push(productionTimeTrace)
      traceList.push(changeOverTimeTrace)
      traceList.push(inBatchChangeOverTimeTrace)
    }

    return traceList
  }, [graphData])

  const layout = {
    height: 630,
    plot_bgcolor: 'transparent',
    paper_bgcolor: 'transparent',
    yaxis: {
      title: {
        text: 'Hours',
        standoff: 1,
      },
      tickmode: 'array',
      linewidth: 1,
      showgrid: true,
      gridcolor: '#CCCCCC',
      gridwidth: 1,
      linecolor: colors.lineColor,
      showline: true,
      tickfont: {
        family: 'proxima-nova',
        size: 14,
        color: colors.fontColor,
      },
    },
    xaxis: {
      type: 'category',
      zeroline: true,
      title: {
        text: 'Week',
      },
      showline: true,
      linewidth: 1,
      linecolor: colors.lineColor,
      tickfont: {
        family: 'proxima-nova',
        size: 14,
        color: colors.fontColor,
      },
    },
    barmode: 'stack',
    autoexpand: false,
    autosize: true,
    annotations: [
      {
        x: 1,
        y: capacityReferenceValue,
        xref: 'paper',
        yref: 'y',
        text: `${'Capacity:' + capacityReferenceValue}h`,
        yanchor: 'bottom',
        showarrow: false,
        ax: 0,
        ay: -40,
        font: {
          color: colors.fontColor,
        },
      },
    ],
    shapes: [
      {
        type: 'line',
        xref: 'paper',
        x0: 0,
        x1: 1,
        yref: 'y',
        opacity: 0.5,
        y0: capacityReferenceValue,
        y1: capacityReferenceValue,
        line: {
          width: 1,
          color: colors.referenceLineColor,
        },
      },
    ],
    legend: {
      orientation: 'h',
      x: 0,
      y: 1.05,
    },
    margin: {
      l: 40,
      r: 10,
      b: 40,
      t: 10,
    },
  }

  // @ts-ignore
  return <Graph data={data} layout={layout} responsive={false} />
}

export const SequencedGraphView = ({
  templateOutputsData,
  templateSummaryData,
}: {
  templateOutputsData: TemplateOutputs[]
  templateSummaryData: TemplateSummaryOutputs[]
}) => {
  const summary = templateSummaryData[0]

  const graphData = convertTemplateOutputsToGraphData(templateOutputsData)

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <SequenceOfEventsGraph
        graphData={graphData}
        capacityReferenceValue={parseFloat(
          summary.workcentreCapacityInitial.toFixed(1)
        )}
        averageUtilisationPercentage={parseFloat(
          summary.utilisationVolatility.toFixed(1)
        )}
        averageUtilisationHours={parseFloat(
          summary.avgUtilisationHoursNonFOQPerWeek.toFixed(1)
        )}
      />
    </Box>
  )
}
